import type { Project } from '@interfaces/IProject';
import { formatYmd, APP_DEFAULT_TZ } from '@utils/dateUtils';
import { PhotoPanel } from './PhotoPanel/PhotoPanel';
import { Performance } from './Performance/Performance';
import { MilestoneStrip } from './MilestoneStrip/MilestoneStrip';
import { MilestoneHighlights } from './MilestoneHighlights/MilestoneHighlights';
import { summarizeMilestones } from './MilestoneHighlights/summarizeMilestones';
import styles from './styles.module.css';

type IProjectCardProps = {
  project: Project;
  onClick?: () => void;
  isFavorite: boolean;
  onToggleFavorite: () => void;
};

/**
 * One project on the portfolio: who and how it stands (the photo panel), then
 * two sections — Performance (four figures as bars) and Milestones (the strip
 * plus the current and next one). The card lays itself out by its OWN width
 * (a size container), not the window's: stacked when narrow, the two sections
 * side by side when wide, the photo a top banner when very narrow.
 */
export function ProjectCard({ project, onClick, isFavorite, onToggleFavorite }: IProjectCardProps) {
  // No schedule came back → the milestone section says so instead of
  // drawing an empty strip (Performance shows its own empty progress bar).
  const scheduleDataUnavailable = project.scheduleStatus === 'noData';
  // The portfolio row carries no site timezone, so "today" is the app's (JST)
  // — the same day the strip's today tooltip already shows.
  const milestones = summarizeMilestones(project.milestoneStrip, formatYmd(new Date(), APP_DEFAULT_TZ));
  // A strip needs two milestones to draw a timeline; with fewer there is
  // nothing to plot, so the section steps aside and the card is shorter.
  const showMilestones = scheduleDataUnavailable || milestones.total >= 2;
  return (
    <article
      className={styles.card}
      data-testid="project-card"
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              // Only the card itself: Enter on the star or a link inside it
              // must do that control's job, not open the monitor.
              if (e.target !== e.currentTarget) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <div className={styles.layout}>
        <PhotoPanel project={project} isFavorite={isFavorite} onToggleFavorite={onToggleFavorite} />

        <div className={styles.body}>
          <Performance project={project} />
          {showMilestones ? (
            <>
              <div className={styles.divider} aria-hidden />
              <section className={styles.milestones} data-testid="milestones">
                <MilestoneStrip strip={project.milestoneStrip} summary={milestones} unavailable={scheduleDataUnavailable} />
                {scheduleDataUnavailable ? null : <MilestoneHighlights summary={milestones} />}
              </section>
            </>
          ) : null}
        </div>
      </div>
    </article>
  );
}
